import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  contentChildren,
  input,
  model,
  output,
} from '@angular/core';
import { WhColumnDirective, WhTableEmptyDirective } from './wh-column.directive';

export interface WhSortEvent {
  key: string;
  dir: 'asc' | 'desc';
  /** `false` only when the tri-state cycle cleared the sort (reachable when `[tristate]` is on).
   *  Bi-state consumers can ignore it — it is always `true` unless `tristate` is enabled. */
  active: boolean;
}

/**
 * Canonical data table for both apps — CDK-free CSS-grid table (the integrator's list-table shape:
 * grid rows, sortable headers, colored cell content) that replaces every hand-rolled `<table>`
 * and the PrimeNG-backed `lib-generic-table`.
 *
 * Columns are declared with `[whColumn]` templates (see WhColumnDirective) so any cell content is
 * possible. Sorting is stateless (parent owns `sortKey`/`sortDir` and reloads on `(sort)`), like
 * `lib-pager`. Selection is a two-way `selection` model. Row grouping (e.g. by org) and the
 * mobile card fallback stay in the PARENT — the table renders a flat set.
 *
 *   <wh-table [rows]="items()" gridTemplate="minmax(0,2fr) 1fr 120px"
 *             [selectable]="true" [(selection)]="selected"
 *             [clickable]="true" (rowClick)="open($event)"
 *             [sortKey]="sortKey()" [sortDir]="sortDir()" (sort)="onSort($event)">
 *     <ng-template whColumn header="Name" key="name" [sortable]="true" let-row>…</ng-template>
 *     <ng-template whColumn header="Status" let-row>…</ng-template>
 *     <ng-template whTableEmpty>No items</ng-template>
 *   </wh-table>
 */
@Component({
  selector: 'wh-table',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block w-full' },
  template: `
    <div class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <div class="overflow-x-auto">
        <div class="min-w-full" [style.min-width]="minWidth()">
          <!-- Header -->
          <div
            class="grid items-center gap-x-4 border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400"
            [style.grid-template-columns]="gridCols()">
            @if (selectable()) {
              <div>
                <input
                  type="checkbox"
                  class="text-primary-500 focus:ring-primary-500 size-4 rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700"
                  [checked]="allSelected()"
                  (change)="toggleAll()" />
              </div>
            }
            @for (col of columns(); track col) {
              <div [class.text-center]="col.align() === 'center'" [class.text-right]="col.align() === 'right'">
                @if (col.sortable() && col.key()) {
                  <button
                    type="button"
                    class="-ml-1 inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-gray-200/60 dark:hover:bg-slate-700"
                    (click)="onSortClick(col)">
                    {{ col.header() }}
                    <span class="text-xs" [class.text-gray-300]="sortKey() !== col.key()">{{ glyph(col) }}</span>
                  </button>
                } @else {
                  {{ col.header() }}
                }
              </div>
            }
          </div>

          <!-- Loading skeleton -->
          @if (loading()) {
            @for (r of skeletonRows; track $index) {
              <div
                class="grid items-center gap-x-4 border-b border-gray-100 px-4 py-3.5 last:border-b-0 dark:border-slate-700/60"
                [style.grid-template-columns]="gridCols()">
                @if (selectable()) {
                  <div class="h-4 w-4 animate-pulse rounded bg-gray-200 dark:bg-slate-700"></div>
                }
                @for (col of columns(); track col) {
                  <div class="h-4 animate-pulse rounded bg-gray-200 dark:bg-slate-700"></div>
                }
              </div>
            }
          } @else {
            <!-- Body -->
            @for (row of rows(); track keyOf(row); let i = $index) {
              <div
                class="grid items-center gap-x-4 border-b border-gray-100 px-4 py-3.5 text-base text-gray-700 transition-colors last:border-b-0 dark:border-slate-700/60 dark:text-slate-200"
                [ngClass]="rowClasses(row)"
                [style.grid-template-columns]="gridCols()"
                (click)="onRowClick(row)"
                (mousedown)="onRowMouseDown($event, row)">
                @if (selectable()) {
                  <div (click)="$event.stopPropagation()">
                    <input
                      type="checkbox"
                      class="text-primary-500 focus:ring-primary-500 size-4 rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700"
                      [checked]="isSelected(row)"
                      (change)="toggleRow(row)" />
                  </div>
                }
                @for (col of columns(); track col) {
                  <div
                    class="min-w-0"
                    [class.text-center]="col.align() === 'center'"
                    [class.text-right]="col.align() === 'right'">
                    <ng-container *ngTemplateOutlet="col.tpl; context: { $implicit: row, row: row, index: i }" />
                  </div>
                }
              </div>
            } @empty {
              <div class="px-4 py-10 text-center text-sm text-gray-400 dark:text-slate-500">
                @if (emptyTpl(); as empty) {
                  <ng-container *ngTemplateOutlet="empty.tpl" />
                } @else {
                  No data
                }
              </div>
            }
          }
        </div>
      </div>
    </div>
  `,
})
export class WhTableComponent<T = unknown> {
  /** Row data (parent-paginated). */
  readonly rows = input.required<readonly T[]>();
  /** Stable identity for track + selection. Defaults to `row.id` (or the row itself). */
  readonly rowKey = input<(row: T) => unknown>(r => (r as { id?: unknown })?.id ?? r);
  /** Explicit `grid-template-columns`. If omitted, derived from each column's `width` (or `1fr`). */
  readonly gridTemplate = input<string | undefined>(undefined);
  /** Optional horizontal min-width for the scroll area (e.g. `'820px'`) so fixed-width columns
   *  don't collapse on narrow viewports — matches the integrator lists' `min-w-[820px]`. */
  readonly minWidth = input<string | undefined>(undefined);
  /** Show skeleton rows instead of data (initial load). */
  readonly loading = input<boolean>(false);

  protected readonly skeletonRows = Array.from({ length: 6 });

  /** Show a checkbox column + select-all. */
  readonly selectable = input<boolean>(false);
  /** Two-way selected rows. */
  readonly selection = model<T[]>([]);

  /** Rows are clickable (cursor + `(rowClick)`). */
  readonly clickable = input<boolean>(false);
  readonly rowClick = output<T>();
  /** Middle-click (mouse-wheel) on a row — parents use it to open the row in a new tab. */
  readonly rowAux = output<T>();

  /** Stateless sort — parent owns state and reloads on `(sort)`. */
  readonly sortKey = input<string | null>(null);
  readonly sortDir = input<'asc' | 'desc'>('asc');
  readonly sort = output<WhSortEvent>();
  /** Tri-state header cycle (asc → desc → cleared), matching the integrator lists. When the cycle
   *  clears, `(sort)` emits `{ active: false }`. Default is the bi-state asc ↔ desc toggle. */
  readonly tristate = input<boolean>(false);

  protected readonly columns = contentChildren(WhColumnDirective);
  protected readonly emptyTpl = contentChild(WhTableEmptyDirective);

  /** Full grid template incl. the leading checkbox track when selectable. */
  protected readonly gridCols = computed(() => {
    const body =
      this.gridTemplate() ??
      this.columns()
        .map(c => c.width() ?? 'minmax(0, 1fr)')
        .join(' ');
    return (this.selectable() ? '44px ' : '') + body;
  });

  /** Conditional row classes via ngClass (multi-class string values keep the slash-containing
   *  dark utilities out of `[class.KEY]` bindings, which Angular can't parse with a `/`). */
  protected rowClasses(row: T): Record<string, boolean> {
    const interactive = this.clickable() || this.selectable();
    return {
      'cursor-pointer': this.clickable(),
      'hover:bg-gray-50 dark:hover:bg-slate-700/40': interactive,
      'bg-primary-50 dark:bg-primary-500/10': this.selectable() && this.isSelected(row),
    };
  }

  protected keyOf(row: T): unknown {
    return this.rowKey()(row);
  }

  protected isSelected(row: T): boolean {
    const k = this.keyOf(row);
    return this.selection().some(s => this.keyOf(s) === k);
  }

  protected toggleRow(row: T): void {
    const k = this.keyOf(row);
    const cur = this.selection();
    this.selection.set(this.isSelected(row) ? cur.filter(s => this.keyOf(s) !== k) : [...cur, row]);
  }

  protected allSelected(): boolean {
    const rows = this.rows();
    return rows.length > 0 && rows.every(r => this.isSelected(r));
  }

  protected toggleAll(): void {
    if (this.allSelected()) {
      const keys = new Set(this.rows().map(r => this.keyOf(r)));
      this.selection.set(this.selection().filter(s => !keys.has(this.keyOf(s))));
    } else {
      const curKeys = new Set(this.selection().map(s => this.keyOf(s)));
      this.selection.set([...this.selection(), ...this.rows().filter(r => !curKeys.has(this.keyOf(r)))]);
    }
  }

  protected onRowClick(row: T): void {
    if (this.clickable()) this.rowClick.emit(row);
  }

  /** Emit `rowAux` on middle-click (button 1). Suppresses the browser autoscroll default so the
   *  parent can open the row in a new tab, mirroring the integrator lists' `onRowMiddleClick`. */
  protected onRowMouseDown(event: MouseEvent, row: T): void {
    if (event.button === 1) {
      event.preventDefault();
      this.rowAux.emit(row);
    }
  }

  protected onSortClick(col: WhColumnDirective): void {
    const key = col.key();
    if (!col.sortable() || !key) return;
    const isCurrent = this.sortKey() === key;
    if (this.tristate()) {
      // asc → desc → cleared → asc
      if (!isCurrent) return this.sort.emit({ key, dir: 'asc', active: true });
      if (this.sortDir() === 'asc') return this.sort.emit({ key, dir: 'desc', active: true });
      return this.sort.emit({ key, dir: 'asc', active: false });
    }
    const dir: 'asc' | 'desc' = isCurrent && this.sortDir() === 'asc' ? 'desc' : 'asc';
    this.sort.emit({ key, dir, active: true });
  }

  protected glyph(col: WhColumnDirective): string {
    if (this.sortKey() !== col.key()) return '↕';
    return this.sortDir() === 'asc' ? '↑' : '↓';
  }
}
